"use client";

import { useEffect, useState } from "react";

export type DeviceType = "ios" | "android" | "unknown";

/** Minimum viewport width for desktop PWA install eligibility (Tailwind `md`). */
const PWA_DESKTOP_MIN_WIDTH_PX = 768;

/** Viewport cap for coarse-pointer-only devices that may be tablets. */
const PWA_COARSE_POINTER_MAX_WIDTH_PX = 1280;

/**
 * True when the client looks like a phone or tablet (not a desktop/laptop).
 * Safe to call only in the browser after mount.
 */
export function detectMobileOrTablet(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent.toLowerCase();
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } })
    .userAgentData;
  if (uaData?.mobile === true) return true;

  if (/iphone|ipod|ipad|android|webos|blackberry|iemobile|opera mini|mobile/.test(ua)) {
    return true;
  }

  // iPadOS 13+ can report as Mac with touch.
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) {
    return true;
  }

  return false;
}

/**
 * True when the PWA install prompt may be shown (desktop/laptop browsers only).
 * Combines UA/platform checks, viewport width, and pointer capabilities.
 */
export function isDesktopEligibleForPwaInstall(): boolean {
  if (typeof window === "undefined") return false;
  if (detectMobileOrTablet()) return false;
  if (window.innerWidth < PWA_DESKTOP_MIN_WIDTH_PX) return false;

  const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const hasFinePointer = window.matchMedia("(pointer: fine)").matches;
  if (
    hasCoarsePointer &&
    !hasFinePointer &&
    window.innerWidth < PWA_COARSE_POINTER_MAX_WIDTH_PX
  ) {
    return false;
  }

  return true;
}

/**
 * Hydration-safe device detection for public install/open-app pages.
 * Uses navigator.userAgent only on the client after mount.
 */
export function useDevice(): DeviceType {
  const [device, setDevice] = useState<DeviceType>("unknown");

  useEffect(() => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent?.toLowerCase() ?? "" : "";
    if (/iphone|ipad|ipod/.test(ua)) {
      setDevice("ios");
    } else if (/android/.test(ua)) {
      setDevice("android");
    } else {
      setDevice("unknown");
    }
  }, []);

  return device;
}

/**
 * Hydration-safe desktop eligibility for the PWA install prompt.
 * Starts false, then updates on mount and on resize.
 */
export function useIsDesktopEligibleForPwaInstall(): boolean {
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    const sync = () => setEligible(isDesktopEligibleForPwaInstall());
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  return eligible;
}
